import { useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast } from "@shared/ui/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import {
  CreateServiceOrderPayload,
  serviceOrderApi,
  ServiceOrderDetail,
  ServiceOrderFilters,
  ServiceOrderListResponse,
} from "../apis/serviceOrderTab";
import { ServiceOrder } from "../types/consumerRequest";

export interface UseServiceOrderReturn {
  // Data
  serviceOrders: ServiceOrder[];
  serviceOrderDetail?: ServiceOrderDetail;
  isLoading: boolean;
  isError: boolean;
  error: any;

  // Mutations
  createServiceOrder: (payload: CreateServiceOrderPayload) => Promise<void>; // Loading states
  isCreating: boolean;

  // Refresh
  refetch: () => void;
}

export function useServiceOrders(
  filters: ServiceOrderFilters,
  options?: {
    enabled?: boolean;
  }
): UseServiceOrderReturn {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);

  // Query for service orders list
  const {
    data: serviceOrdersData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["serviceOrders", filters, currentPage],
    queryFn: () =>
      serviceOrderApi.getServiceOrders({ ...filters, page: currentPage }),
    enabled: !!filters?.request_id && filters.request_id !== "NA" && !!filters?.remote_utility_id,
  });

  // Create service order mutation
  const createMutation = useMutation({
    mutationFn: serviceOrderApi.createServiceOrder,
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Service order created successfully",
        variant: "default",
      });
      queryClient.invalidateQueries({ queryKey: ["serviceOrders"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create service order",
        variant: "destructive",
      });
    },
  });

  // Helper functions
  const createServiceOrder = useCallback(
    async (payload: CreateServiceOrderPayload) => {
      await createMutation.mutateAsync(payload);
    },
    [createMutation]
  );

  return {
    // Data
    serviceOrders:
      (serviceOrdersData as ServiceOrderListResponse)?.result || [],
    isLoading,
    isError,
    error,

    // Mutations
    createServiceOrder,

    // Loading states
    isCreating: createMutation.isPending,

    // Refresh
    refetch,
  };
}
// Hook for service order choices
export function useServiceOrderChoices(remoteUtilityId: number) {
  const { data: types, isLoading: isLoadingTypes } = useQuery({
    queryKey: ["serviceOrderTypes"],
    queryFn: () =>
      serviceOrderApi.getServiceOrderTypes({
        remoteUtilityId: remoteUtilityId,
      }),
  });

  const { data: statuses, isLoading: isLoadingStatuses } = useQuery({
    queryKey: ["serviceOrderStatuses"],
    queryFn: () => serviceOrderApi.getServiceOrderStatuses(),
  });

  const { data: priorities, isLoading: isLoadingPriorities } = useQuery({
    queryKey: ["serviceOrderPriorities"],
    queryFn: () => serviceOrderApi.getServiceOrderPriorities(),
  });

  return {
    types: (types as { result: any[] })?.result || [],
    statuses: (statuses as { result: any[] })?.result || [],
    priorities: (priorities as { result: any[] })?.result || [],
    isLoading: isLoadingTypes || isLoadingStatuses || isLoadingPriorities,
  };
}

export const useJobCardPriority = () => {
  return useSmartQuery(QueryKeyFactory.module.cx.jobcardPriorityOptions(), () =>
    serviceOrderApi.getJobCardPriorities()
  );
};
